/* eslint-disable prefer-template */
import './SurveyBuilderQuestionAccordion.scss'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import {
    makeObservable, observable, action,
} from 'mobx'
import AnimateHeight from 'react-animate-height'
import i18n from 'i18next'
import * as R from 'ramda'

import GrabIcon from '../../../assets/images/move-icon.svg'
import Menu from '../../../UniformComponents/Menu'
import HoverInfo from '../../../UniformComponents/HoverInfo'
import Tooltip from '../../../UniformComponents/Tooltip'
import Button from '../../../UniformComponents/Button'
import Typography from '../../../UniformComponents/Typography'
import Checkbox from '../../form/Checkbox'
import Select from '../../form/Select'
import DebugInfo from '../../ReportV2/components/RightPanel/DebugInfo'
import { trackBuilderQuestionMenuAction } from '../../../utils/MixpanelManager'

import * as SkipLogicPreview from '../../SkipLogic/Preview'

const menuActions = Object.freeze({
    REQUIRED: 'required',
    COPY: 'copyQuestion',
    EDIT: 'editQuestion',
    SKIP: 'skip',
    LOCK: 'lock',
    SHOW: 'show',
    INHERIT: 'inherit',
    DELETE: 'delele',
    MAP: 'map',
    IS_AI_CODING_DISABLED: 'isAiCodingDisabled',
    IS_SI_OBJECTIVES_QUESTION: 'isSiObjectivesQuestion',
})

@observer
export default class SurveyBuilderQuestionAccordion extends React.Component {
    @observable open = false

    @observable menuOpen = false

    @observable openTab = null

    qListInfo = {}

    parentPageQuestionsIds = new Set()

    hiddenPageQuestionsIds = new Set()

    constructor(props) {
        super(props)
        makeObservable(this)
        this.setRef()
        this.getQListInfo()
    }

    componentDidUpdate(prevProps) {
        const { isLite, isFailed, isDragging } = this.props
        if (isLite || isFailed) return
        if (isDragging && !prevProps.isDragging && this.open) {
            this.togglePreview()
        }
    }

    @action
    setRef = () => {
        const { question, store, isLite } = this.props
        if (isLite) return // isLite does not have store
        store.iframeRefs.set(question.id, {
            ref: React.createRef(),
            height: 'auto',
        })
    }

    getQListInfo = () => {
        const info = {
            mandatory: {
                isMandatory: false,
                menuTooltipText: '',
                toolbarTooltipText: '',
            },
            recommended: {
                isRecommended: false,
                toolbarTooltipText: '',
            },
        }
        const { question } = this.props
        const { questionId } = question
        const { store } = this.props
        if (!store?.survey?.questionLibrary) return info
        const { questionLibraryStore } = store.survey.questionLibrary

        const mandatoryQuestions = questionLibraryStore.questionLibraryLists.get('mandatory')
        const isMandatory = mandatoryQuestions.find(
            (mq) => mq.questionId === questionId.toUpperCase(),
        )
        if (isMandatory) {
            info.mandatory.isMandatory = true
            const text = i18n.t(
                'questionLibrary.question.option.infoText.disabled_mandatory',
                {
                    productType: isMandatory.producttypename,
                    respondentType: isMandatory.respondenttypename,
                },
            )
            info.mandatory.menuTooltipText = text
            info.mandatory.toolbarTooltipText = text
        }

        const recommendedQuestions = questionLibraryStore.questionLibraryLists.get('recommended')
        const isRecommended = recommendedQuestions.find(
            (mq) => mq.questionId === questionId.toUpperCase(),
        )
        if (isRecommended) {
            info.recommended.isRecommended = true
            info.recommended.menuTooltipText = i18n.t('questionLibrary.question.option.recommended')
        }
        this.qListInfo = info
    }

    getUpdatedSurveyInfo = () => {
        const { store } = this.props
        const { survey } = store
        if (!survey) return []

        const pageQuestions = survey.pages.flatMap((p) => p.questions)
        const questionData = pageQuestions.reduce((ids, pq) => {
            if (!pq) return ids
            if (pq.parentId) ids.parentPageQuestionsIds.add(pq.parentId)
            if (pq.isHidden) ids.hiddenPageQuestionsIds.add(pq.id)
            if (pq.populateFrom) {
                const defaultLocale = R.pathOr('', ['survey', 'metadata', 'defaultLocale'], store)
                const shortTitle = pq.question.content.getTitleByLocale('shortTitle', store.surveyLocale, defaultLocale)
                ids.mappedQuestionsIds.set(
                    pq.questionId,
                    shortTitle,
                )
            }
            return ids
        }, {
            parentPageQuestionsIds: new Set(), // questions providing answer options to other questions in this survey
            hiddenPageQuestionsIds: new Set(),
            mappedQuestionsIds: new Map(),
        })

        this.parentPageQuestionsIds = questionData.parentPageQuestionsIds
        this.hiddenPageQuestionsIds = questionData.hiddenPageQuestionsIds
        store.surveyQuestionsIdsWithMapping = questionData.mappedQuestionsIds
    }

    @action
    togglePreview = () => {
        if (this.open && this.openTab === 'preview') {
            this.openTab = null
            this.open = false
        } else {
            const { store, question, isLite } = this.props
            if (isLite) return
            const iframeRef = store.iframeRefs.get(question.id)
            store.iframeRefs.set(question.id, {
                ref: iframeRef.ref,
                height: 'auto',
            })
            this.openTab = 'preview'
            this.open = true
        }
    }

    setOpenTab = (tab) => {
        if (this.openTab === tab && this.open) {
            this.open = false
            this.openTab = null
            return
        }

        this.openTab = tab
        if (!this.open) this.open = true
    }

    previewLink = () => {
        const { store, question, page } = this.props
        const { survey, useLatestUI } = store
        const prot = window.location.protocol
        console.log(`question previewLink ${useLatestUI}`)
        let url = window.location.host.split('.')
        if (url[0].match(/dev/)) {
            url[0] = 'surveys.staging'
        } else if (url.includes('staging') && url[0] !== 'admin') {
            url.splice(0, 2, 'surveys')
        } else {
            url[0] = 'surveys'
        }
        url = url.join('.')
        return `${prot}//${url}/?surveyUuid=${survey.publicId}&pageId=${page.id}&questionId=${question.id}${useLatestUI ? '&version=v2' : ''}&langcode=${store.surveyLocale}`
    }

    renderContent = () => {
        const { question, store, inheritParent } = this.props
        const iframeRef = !store ? null : store.iframeRefs.get(question.id)
        const locale = R.pathOr('', ['surveyLocale'], store)
        const defaultLocale = R.pathOr('', ['survey', 'metadata', 'defaultLocale'], store)
        const locales = {
            locale,
            defaultLocale,
        }

        switch (this.openTab) {
            case 'preview':
                return (
                    <object
                        ref={iframeRef}
                        style={{
                            width: '100%',
                            marginLeft: '0em',
                            height: iframeRef ? iframeRef.height : 0,
                            maxHeight: 450,
                        }}
                        data={this.previewLink()}
                        aria-label="question preview"
                    />
                )
            case 'option':
                return SkipLogicPreview.showOption(store.survey, question, locales)
            case 'page':
                return SkipLogicPreview.skipToPage(store.survey, question, locales)
            case 'question':
                return SkipLogicPreview.showQuestion(store.survey, question, locales)
            case 'inherited':
                return (
                    <div>
                        <div className="printShowIf survey-builder-option-tab-options">
                            <span>
                                {i18n.t('s_answer_selected')}
                            </span>

                            <span style={{ fontWeight: 'bold' }}>
                                {inheritParent.label}
                            </span>

                            <span>
                                {i18n.t('s_will_be_inherited')}
                            </span>
                        </div>
                    </div>
                )
            case 'mapping': {
                const { survey } = store
                const mappedFromId = question.populateFrom.pageQuestionId
                if (!mappedFromId) return null
                const mappedFromQuestion = survey.pages
                    .flatMap((p) => p.questions ?? [])
                    .find((pq) => pq.id === mappedFromId)
                if (!mappedFromQuestion) return null
                const questionTitle = mappedFromQuestion.question.content
                    .getTitleByLocale('shortTitle', locale, defaultLocale)
                return (
                    <div className="printShowIf survey-builder-option-tab-options">
                        <p>
                            {i18n.t('s_mapping_answers_from_pt1')}
                            {' '}
                            <b>{questionTitle}</b>
                            {' '}
                            {i18n.t('s_mapping_answers_from_pt2')}
                        </p>
                    </div>
                )
            }
            default:
                return null
        }
    }

    toggleMenu = (val) => {
        if (val) this.menuOpen = val
        else this.menuOpen = !this.menuOpen
    }

    execMenuAction = (func) => {
        this.toggleMenu(false)
        this.openTab = ''
        func()
    }

    shouldHideOptions = (store, key, question) => {
        // hide options for company and explori level questions (except show/hide/edit question) on Lite surveys
        const questionLevel = R.pathOr(1, ['question', 'metadata', 'oid'], question)
        return (questionLevel <= 3
            && ![menuActions.SHOW, menuActions.DELETE, menuActions.EDIT].includes(key)
            && store.survey.metadata.isRapidSurvey)
            || (
                !!question.usedInParentSurvey
                && ![
                    menuActions.SHOW,
                    menuActions.COPY,
                    menuActions.REQUIRED,
                    menuActions.LOCK,
                    menuActions.SKIP,
                    menuActions.INHERIT,
                ].includes(key)
            )
    }

    hideMenuItem = (item) => {
        item.isVisible = false
        item.isDisabled = true
    }

    getMenuItemConfig = (key, question) => {
        const { store } = this.props
        const { survey } = store
        const item = {
            isVisible: true, isDisabled: false, tooltip: '', disabledReasons: [], rapidDisabled: false,
        }
        const hideOptions = this.shouldHideOptions(store, key, question)
        if (hideOptions) {
            this.hideMenuItem(item)
            return item
        }
        const isSurveyClosed = store && store.survey.closed
        const isLocked = !question?.canBeEdited
        const prefix = 'questionLibrary.question.option.infoText.'

        switch (key) {
            case menuActions.REQUIRED: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.required.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'required.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'required')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.LOCK: {
                if (store.isMasterSuper()) {
                    if (isLocked) item.tooltip = i18n.t(prefix + 'lock.locked.master')
                    else item.tooltip = i18n.t(prefix + 'lock.unlocked.master')
                } else if (isLocked) {
                    item.tooltip = (i18n.t(prefix + 'lock.locked'))
                    item.isDisabled = true
                } else {
                    item.isVisible = false
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.EDIT: {
                // previous rule
                // item.isVisible = question.isEditable && question.modifiable && question.deletable
                const { isEditable, editMode } = question
                const questionLevel = R.pathOr(undefined, ['question', 'metadata', 'oid'], question)
                if (!isEditable || editMode === 'None') {
                    item.disabledReasons.push(i18n.t(prefix + 'edit.disabled_core'))
                    item.isDisabled = true
                }
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                // if item is disabled at this point it's disabled for a reason
                // beyond rapid so rapid disabled should be false
                if (!item.isDisabled
                    && survey.metadata.isRapidSurvey
                    && questionLevel !== 6 // survey questions are always editable
                    && survey.rapidSurveyRemainingQuestions < 1
                ) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                // end
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'edit.disabled_desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.DELETE: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }
                // COMMENTING OUT INCASE WE WANT TO REVERT LATER
                // if (survey.metadata.isRapidSurvey) {
                //     item.disabledReasons.push(
                //         i18n.t('questionLibrary.question.option.infoText.delete.rapid_disabled'),
                //     )
                //     item.isDisabled = true
                //     item.rapidDisabled = true
                // }
                if (item.isDisabled && !isSurveyClosed) {
                    item.tooltip = i18n.t(prefix + 'delete.disabled_desc')
                }
                if (isSurveyClosed) item.isDisabled = true

                return item
            }
            case menuActions.SKIP: {
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'skiplogic.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'skiplogic.desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.SHOW: {
                if (isLocked) {
                    item.tooltip = i18n.t(prefix + 'show.disabled_desc')
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                if (question.question?.questionTypeCode === 'UV') {
                    item.tooltip = i18n.t(prefix + 'show.disabled_desc')
                    item.disabledReasons.push(i18n.t(prefix + 'show.url_variable_disabled'))
                    item.isDisabled = true
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.INHERIT: {
                const { inheritsAllowed } = this.props
                if (isLocked) {
                    item.disabledReasons.push(i18n.t(prefix + 'disabled_locked'))
                    item.isDisabled = true
                }
                const { mandatory } = this.qListInfo
                if (mandatory.isMandatory) {
                    item.disabledReasons.push(mandatory.menuTooltipText)
                    item.isDisabled = true
                }

                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                if (question.usedInParentSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.branch.inheritDisabled1'),
                        i18n.t('questionLibrary.question.option.infoText.branch.inheritDisabled2'),
                    )
                    item.isDisabled = true
                }

                // target question type check
                const validQuestionTypes = inheritsAllowed.to
                if (!validQuestionTypes.includes(question.question?.questionTypeCode)) {
                    item.disabledReasons.push(i18n.t(prefix + 'inherit.disabled_qtype'))
                    item.isDisabled = true
                }
                // prev questions type check
                const pageQuestion = store.survey?.pages.find(
                    (p) => p.questions.some((q) => q.questionId === question.questionId),
                )
                const prevTypes = new Set(pageQuestion.prev_questions.map((q) => q.type))
                const arePrevQuestionsValid = validQuestionTypes.some((type) => prevTypes.has(type))
                if (!arePrevQuestionsValid) {
                    item.disabledReasons.push(i18n.t(prefix + 'inherit.disabled_na_questions'))
                    item.isDisabled = true
                }
                if (item.isDisabled) {
                    item.tooltip = i18n.t(prefix + 'inherit.disabled_desc')
                } else {
                    item.tooltip = i18n.t(prefix + 'inherit.enabled_desc')
                }
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.COPY: {
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }

                if (item.isDisabled) {
                    item.tooltip = i18n.t('questionLibrary.question.option.infoText.copy.disabled_desc')
                }

                return item
            }
            case menuActions.MAP: {
                const { isHidden } = question
                const { mapsAllowed } = this.props
                if (!isHidden) {
                    item.isDisabled = true
                    item.isVisible = false
                }
                if (this.parentPageQuestionsIds.has(question.id)) {
                    item.isDisabled = true
                    item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.provides_answer_options'))
                }
                if (!R.isNil(question.parentId)) {
                    item.isDisabled = true
                    item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.child_question'))
                }
                if (survey.metadata.isRapidSurvey) {
                    item.disabledReasons.push(
                        i18n.t('questionLibrary.question.option.infoText.generic.rapid_disabled'),
                    )
                    item.isDisabled = true
                    item.rapidDisabled = true
                }
                const validQuestionTypes = mapsAllowed[question.question?.questionTypeCode]
                if (!validQuestionTypes) {
                    // means this question does not accept mapping
                    item.isVisible = false
                } else {
                    const pageIndex = store.survey?.pages.findIndex(
                        (p) => p.questions.some((q) => q.questionId === question.questionId),
                    )
                    const pagesBefore = pageIndex > 0 ? store.survey.pages.slice(0, pageIndex) : []
                    const allPrevPageQuestions = pagesBefore.flatMap((page) => page.questions)
                    const currPage = store.survey.pages[pageIndex]
                    currPage.questions.some((pageQuestion) => {
                        if (pageQuestion.questionId !== question.questionId) {
                            allPrevPageQuestions.unshift(pageQuestion)
                            return false
                        }
                        return true // exit loop after current question is found on page
                    })
                    const prevTypes = new Set(allPrevPageQuestions.map((q) => q.question?.questionTypeCode))
                    const arePrevQuestionsValid = validQuestionTypes.some((type) => prevTypes.has(type))
                    if (!arePrevQuestionsValid) {
                        // means this question does not accept mapping from previous question types
                        item.disabledReasons.push(i18n.t(prefix + 'addMapping.disabled.na_questions'))
                        item.isDisabled = true
                    }
                }
                if (item.isDisabled) item.tooltip = i18n.t(prefix + 'addMapping.disabled_desc')
                else item.tooltip = i18n.t(prefix + 'addMapping.enabled_desc')
                return item
            }
            case menuActions.IS_AI_CODING_DISABLED: {
                if (isSurveyClosed || !['TX', 'TA'].includes(question.question?.questionTypeCode)) this.hideMenuItem(item)
                return item
            }
            case menuActions.COPY: {
                if (isSurveyClosed) this.hideMenuItem(item)
                return item
            }
            case menuActions.IS_SI_OBJECTIVES_QUESTION: {
                if (question.question?.questionTypeCode !== 'TB' || !store.isProductSalesIntelligenceEnabled) {
                    this.hideMenuItem(item)
                }
                return item
            }
            default:
                return item
        }
    }

    createBaseMenu = () => {
        const {
            question,
            store,
            edit,
            copy,
            remove,
            makeMandatory,
            toggleIsAiCodingDisabled,
            skip,
            lock,
            disable,
            inheritValue,
            inheritOptions,
            setInherit,
            pipeQuestion,
            setObjectivesQuestion,
        } = this.props

        const menuList = (
            [
                {
                    compName: menuActions.EDIT,
                    icon: 'fa-light fa-pen-to-square',
                    label: i18n.t('edit_question'),
                    onClick: () => this.execMenuAction(edit),
                },
                {
                    compName: menuActions.COPY,
                    icon: 'fa-light fa-copy',
                    label: i18n.t('questionBuilder.optionsMenu.copy'),
                    onClick: () => this.execMenuAction(copy),
                },
                {
                    compName: menuActions.IS_AI_CODING_DISABLED,
                    icon: 'fa-light fa-sparkles',
                    label: i18n.t('questionBuilder.optionsMenu.isAiCodingDisabled'),
                    menuType: 'checkbox',
                    value: question.isAiCodingDisabled,
                    onClick: () => this.execMenuAction(toggleIsAiCodingDisabled),
                },
                {
                    compName: menuActions.IS_SI_OBJECTIVES_QUESTION,
                    icon: 'fa-light fa-circle-star',
                    label: 'Sales intelligence objectives question',
                    menuType: 'checkbox',
                    value: store.survey?.metadata?.sIObjectivesQuestionId === question.questionId,
                    onClick: () => this.execMenuAction(setObjectivesQuestion),
                },
                {
                    compName: menuActions.REQUIRED,
                    icon: 'fa-light fa-circle-star',
                    label: i18n.t('questionBuilder.optionsMenu.required'),
                    menuType: 'checkbox',
                    value: question.isMandatory,
                    onClick: () => this.execMenuAction(makeMandatory),
                },
                {
                    compName: menuActions.SKIP,
                    icon: 'fa-light fa-arrows-repeat',
                    label: i18n.t('questionBuilder.optionsMenu.addSkipLogic'),
                    onClick: () => this.execMenuAction(skip),
                },
                {
                    compName: menuActions.LOCK,
                    icon: question.canBeEdited ? 'fal fa-user-lock' : 'fas fa-user-lock',
                    label: store.isMasterSuper()
                        ? i18n.t(question.canBeEdited ? 's_alt_q_lock' : 's_alt_q_unlock')
                        : i18n.t('s_alt_q_islocked'),
                    onClick: () => this.execMenuAction(lock),
                },
                {
                    compName: menuActions.SHOW,
                    icon: question.isHidden ? 'fa-light fa-eye' : 'fa-light fa-eye-slash',
                    label: i18n.t(question.isHidden ? 's_alt_q_show' : 's_alt_q_hide'),
                    onClick: () => this.execMenuAction(disable),
                },
                {
                    compName: menuActions.MAP,
                    icon: 'fa-light fa-map',
                    label: i18n.t('questionLibrary.question.option.infoText.addMapping.label'),
                    onClick: () => this.execMenuAction(pipeQuestion),
                },
                {
                    compName: menuActions.INHERIT,
                    icon: 'fa-light fa-diagram-project',
                    label: i18n.t('questionLibrary.question.option.inherit'),
                    menuType: 'menu',
                    onOpen: () => trackBuilderQuestionMenuAction(store, { action: 'open logic menu', logic_type_menu: 'question display/logic' }),
                    list: [
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="inherit-question-title">
                                <p>
                                    {i18n.t('questionLibrary.question.option.subMenuTitle.inherit')}
                                </p>
                            </div>
                            <Select
                                key={inheritValue || 'none'}
                                placeholder={i18n.t('questionLibrary.question.option.inheritPlaceholder')}
                                name="parentQuestion"
                                label={i18n.t('questionLibrary.question.option.inheritLabel')}
                                onBlur={setInherit}
                                value={inheritOptions.find((o) => o.value === inheritValue)}
                                options={inheritOptions
                                    .filter((o) => !this.hiddenPageQuestionsIds.has(o.value))}
                            />
                        </div>,
                        <div>
                            <div
                                className="option-line"
                                style={{ display: !inheritValue ? 'none' : undefined }}
                                onClick={() => {
                                    setInherit({ value: null })
                                    question.refresh(store)
                                }}
                            >
                                <i className="fa-light fa-circle-xmark" />
                                <p dangerouslySetInnerHTML={{ __html: i18n.t('remove') }} />
                            </div>
                        </div>,
                    ],
                    iconShow: true,
                },
                {
                    compName: menuActions.DELETE,
                    label: i18n.t('del_question'),
                    icon: 'fa-light fa-circle-xmark',
                    greyBg: true,
                    onClick: () => this.execMenuAction(remove),
                },
            ]
        )
        return menuList
    }

    addUILogic = (menuItem) => {
        const { question } = this.props
        const item = this.getMenuItemConfig(menuItem.compName, question)
        return Object.assign({}, menuItem, {
            disabled: item.isDisabled,
            menuShow: item.isVisible,
            rapidDisabled: item.rapidDisabled,
            menuTooltip: item.disabledReasons.length === 0
                ? item.tooltip
                : `${item.tooltip}<br><ul>${item.disabledReasons.map((reason) => `<li>${reason}</li>`).join('')}</ul>`,
        })
    }

    getMenu = () => {
        this.getUpdatedSurveyInfo()
        const menuOptions = this.createBaseMenu().map((opt) => this.addUILogic(opt))
            .filter((config) => config.menuShow)
        return (
            menuOptions.map((config) => {
                switch (config.menuType) {
                    case 'checkbox':
                        return (
                            config.rapidDisabled
                                ? (
                                    <div
                                        className="option-line disabled"
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    >
                                        <div className="option-line">
                                            <i className={config.icon} />
                                            <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                            {config.menuTooltip && (
                                                <HoverInfo
                                                    cssClass="hover-info"
                                                    title={config.menuTooltipTitle}
                                                    desc={config.menuTooltip}
                                                    dir="bottom"
                                                >
                                                    <i className="info-icon fa-solid fa-circle-question" />
                                                </HoverInfo>
                                            )}
                                        </div>

                                        <i className="far fa-lock-keyhole" style={{ fontSize: 16 }} />
                                    </div>
                                ) : (
                                    <div className={`option-line ${config.disabled ? 'disabled' : ''}`}>
                                        <i className={config.icon} />
                                        <Checkbox
                                            name={config.label}
                                            label={config.label}
                                            labelDir="left"
                                            cssClass="option-check"
                                            tooltipTitle={config.menuTooltipTitle}
                                            tooltipText={config.menuTooltip}
                                            tooltipDir="bottom"
                                            checked={config.value}
                                            disabled={config.disabled}
                                            onBlur={config.onClick}
                                            stopPropagation
                                        />
                                    </div>
                                )
                        )
                    case 'menu':
                        return (
                            <Menu
                                cssClass="sub-menu"
                                list={config.list}
                                width="400px"
                                dir="left"
                                disabled={config.disabled}
                                showTick
                                hasUniqueLast
                                onOpen={config.onOpen}
                            >
                                <div className={`option-line ${config.disabled ? 'disabled' : ''}`}>
                                    <i className={config.icon} />
                                    <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                    {config.menuTooltip
                                        && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                title={config.menuTooltipTitle}
                                                desc={config.menuTooltip}
                                                dir="bottom"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                </div>
                            </Menu>
                        )
                    default:
                        return (
                            <div className="question-item">
                                <div
                                    className={`option-line ${config.disabled ? 'disabled' : ''}`}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    onClick={config.disabled ? null : config.onClick}
                                >
                                    <div className="option-line">
                                        <i className={config.icon} />
                                        <p dangerouslySetInnerHTML={{ __html: i18n.t(config.label) }} />
                                        {config.menuTooltip && (
                                            <HoverInfo
                                                cssClass="hover-info"
                                                title={config.menuTooltipTitle}
                                                desc={config.menuTooltip}
                                                dir="bottom"
                                            >
                                                <i className="info-icon fa-solid fa-circle-question" />
                                            </HoverInfo>
                                        )}
                                    </div>

                                    {config.rapidDisabled && (
                                        <i className="far fa-lock-keyhole" style={{ fontSize: 16 }} />
                                    )}
                                </div>
                            </div>
                        )
                }
            })
        )
    }

    getMandatoryOrRecommendedIcons = () => {
        const { recommended, mandatory } = this.qListInfo
        const { isMandatory } = mandatory
        const { isRecommended } = recommended
        let tooltip; let icon

        if (isMandatory) {
            tooltip = mandatory.menuTooltipText
            icon = 'fa-light fa-shield-check'
        } else if (isRecommended) {
            tooltip = recommended.menuTooltipText
            icon = 'fa-light fa-thumbs-up'
        }

        if (!isMandatory && !isRecommended) return null
        return (
            <div className="icon-tooltip">
                <Tooltip
                    text={tooltip}
                    direction="up"
                    style={{ width: 200 }}
                />
                <i className={icon} />
            </div>
        )
    }

    renderGrabIcon = (isEditable, asDivs = false) => {
        let el = null
        if (asDivs) {
            el = (
                <div className="grab-icon">
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                    <div />
                </div>
            )
        } else if (isEditable) {
            el = <img src={GrabIcon} alt="Grab icon" />
        } else {
            el = (
                <div className="icon-tooltip" style={{ paddingTop: '8px' }}>
                    <Tooltip
                        text={i18n.t('s_alt_q_islocked')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-lock" />
                </div>
            )
        }
        return el
    }

    renderLeftIcons = (question) => {
        if (!question) return null
        const icons = []

        if (question.fetchFailed
            && question.question
            && !['ST', 'UV'].includes(question.question.questionTypeCode)
        ) {
            icons.push(
                <i key="iconFailed" className="fa-light fa-circle-star" style={{ fontSize: 18 }} />,
            )
        }

        icons.push(<Fragment key="iconMandatoryRecommended">{this.getMandatoryOrRecommendedIcons()}</Fragment>)

        if (question.usedInOtherSurveys && !question.usedInParentSurvey) {
            icons.push(
                <div key="iconLink" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('s_alt_q_linked')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-link" />
                </div>,
            )
        }
        if (question.usedInParentSurvey) {
            icons.push(
                <div key="iconLink" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('s_alt_q_parent')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-code-branch" />
                </div>,
            )
        }

        if (question.hasPrepopulatedData) {
            icons.push(
                <div key="iconPrepopulated" className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('s_alt_q_contains_reg_data')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <i className="fa-light fa-upload" />
                </div>,
            )
        }

        return icons
    }

    renderRightIcons = (question) => {
        if (!question) return null
        const icons = []

        { /* the "isMandatory" below means "required to answer" */ }
        if (question.isMandatory) {
            icons.push(
                <div className="icon-tooltip">
                    <Tooltip
                        text={i18n.t('questionLibrary.question.option.tooltip.makeRequired')}
                        direction="up"
                        style={{ width: 200 }}
                    />
                    <Typography type="p" color="grey">{i18n.t('s_required')}</Typography>
                </div>,
            )
        }
        return icons
    }

    renderQuestionItemFull = () => {
        const {
            question,
            store,
            provided,
            titleContent,
            oid,
            edit,
            page,
        } = this.props
        const className = (!question.modifiable ? ' locked' : '')
        const menuOptions = this.getMenu()
        const isHidden = question.isHidden || page.isHidden
        const { isRapidSurvey } = store.survey.metadata

        const hasPageLogic = store.survey.skipLogic.find((sl) => (sl.action.kind === 'SkipToPage' && R.pathOr(null, ['triggerGroups', 0, 'children', 0, 'triggers', 0, 'condition', 'pageQuestionId'], sl) === R.pathOr('', ['id'], question)))
        const hasDisplayLogic = question.hasSkipLogic(store) && question.hasSkipLogic(store).action.kind === 'ShowQuestion'
        const hasInheritedOptions = question.parentId && !question.parentId.match(/^[0|-]*$/)
        const hasMappings = question.hasPopulateFrom

        const showQuestionSettingsBar = (
            hasPageLogic
            || hasDisplayLogic
            || hasInheritedOptions
            || question.hasOptionSkipLogic
            || hasMappings
        )
        // exploriAdmins can interact with closed surveys only to map responses
        const hide3DotMenu = store?.survey.closed && !this.getMenuItemConfig(menuActions.MAP, question).isVisible

        return (
            <div
                className={`question-list-item-presentation ${showQuestionSettingsBar ? 'has-footer' : ''}`}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
            >
                <div className={`survey-builder-question-list-item-header-presentation ${store.showDebug ? 'show-debug' : ''}`}>
                    <div className={`question-list-item-left-presentation q_${oid}`} />
                    <div className="grab-container">
                        {this.renderGrabIcon(question.canBeEdited)}
                    </div>
                    <div className="question-list-item-right-presentation">
                        <div className={`
                            question-list-item-right-top-presentation
                            ${isHidden ? 'isHidden' : ''}
                            ${this.open ? 'expanded' : ''}`}
                        >
                            <span className={`survey-builder-question-header-row ${className}`}>
                                <div className="survey-builder-icons-container title-container">
                                    {this.renderLeftIcons(question)}

                                    <div className="title-inner-container" onClick={this.togglePreview}>
                                        <Typography type="h4" cssClass={isHidden ? 'isHidden' : ''}>
                                            {titleContent}
                                        </Typography>
                                        {isHidden && (
                                            <div className="icon-tooltip">
                                                <Tooltip
                                                    text={i18n.t('s_alt_q_isHidden')}
                                                    direction="up"
                                                    style={{ width: 200 }}
                                                />
                                                <i className="fa-light fa-eye-slash" style={{ color: 'grey' }} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="survey-builder-icons-container">
                                    {this.renderRightIcons(question)}

                                    {!this.getMenuItemConfig(menuActions.EDIT, question).isDisabled && (
                                        <Button
                                            label={i18n.t('edit')}
                                            cssClass="question-btn"
                                            onClick={edit}
                                        />
                                    )}

                                    {!!this.getMenuItemConfig(menuActions.EDIT, question).rapidDisabled && (
                                        <HoverInfo
                                            cssClass="hover-info w-300"
                                            desc={i18n.t('surveyBuilder.rapidSurvey.button.disabledTooltipRapidEdit')}
                                            dir="left"
                                        >

                                            <Button
                                                label={i18n.t('edit')}
                                                cssClass="question-btn"
                                                onClick={edit}
                                                disabled
                                                style={{ backgroundColor: '#CADAE1' }}
                                            />
                                        </HoverInfo>
                                    )}

                                    <div className={`options-container options-handler-container ${this.menuOpen ? 'focused' : ''}`} onClick={this.toggleMenu}>
                                        {hide3DotMenu ? (
                                            null
                                        ) : (
                                            <Menu
                                                cssClass="option-menu"
                                                list={menuOptions}
                                                minWidth="375px"
                                                dir="left"
                                                hasUniqueLast={this.getMenuItemConfig(menuActions.DELETE, question).isVisible}
                                            >
                                                <div className="menu-icon">
                                                    <i className="fa-regular fa-ellipsis-vertical" />
                                                </div>
                                            </Menu>
                                        )}
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div>
                            {showQuestionSettingsBar
                                && (
                                    <div className={`question-list-item-right-bottom-presentation ${this.open ? 'expanded' : ''}`}>
                                        <div className="question-preview-presentation">
                                            {hasPageLogic
                                                ? (
                                                    <div
                                                        className={`survey-builder-tab ${this.openTab === 'page' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('page')}
                                                    >
                                                        <span className={this.openTab === 'page' ? 'expanded' : ''}>{i18n.t('pageLogic')}</span>
                                                    </div>
                                                ) : null}

                                            {hasDisplayLogic
                                                ? (
                                                    <div
                                                        className={`survey-builder-tab ${this.openTab === 'question' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('question')}
                                                    >
                                                        <span className={this.openTab === 'question' ? 'expanded' : ''}>{i18n.t('displayLogic')}</span>
                                                    </div>
                                                ) : null}

                                            {hasInheritedOptions
                                                ? (
                                                    <div
                                                        className={`survey-builder-tab ${this.openTab === 'inherited' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('inherited')}
                                                    >
                                                        <span className={this.openTab === 'inherited' ? 'expanded' : ''}>{i18n.t('s_alt_inherited_options')}</span>
                                                    </div>
                                                ) : null}

                                            {question.hasOptionSkipLogic
                                                ? (
                                                    <div
                                                        className={`survey-builder-tab ${this.openTab === 'option' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('option')}
                                                    >
                                                        <span className={this.openTab === 'option' ? 'expanded' : ''}>{i18n.t('s_alt_option_logic')}</span>
                                                    </div>
                                                ) : null}
                                            {hasMappings
                                                ? (
                                                    <div
                                                        className={`survey-builder-tab ${this.openTab === 'mapping' ? 'active' : ''}`}
                                                        onClick={() => this.setOpenTab('mapping')}
                                                    >
                                                        <span className={this.openTab === 'mapping' ? 'expanded' : ''}>{i18n.t('s_alt_q_isMapped')}</span>
                                                    </div>
                                                ) : null}

                                        </div>
                                    </div>
                                )}
                            <AnimateHeight
                                duration={250}
                                height={this.open ? 'auto' : 0}
                                className={this.open ? 'question-content' : ''}
                            >
                                <div className="question-content-preview border-style">
                                    {this.renderContent()}
                                </div>
                            </AnimateHeight>
                        </div>
                    </div>
                </div>
                <DebugInfo
                    store={store}
                    hideDataLink
                    hideCloneLink
                    questionTypeCode={question?.question?.questionTypeCode}
                    questionId={question.questionId}
                    pageQuestionId={question.id}
                />
            </div>
        )
    }

    renderQuestionItemFailed = () => {
        const {
            question,
            titleContent,
            className,
            reloadQuestion,
            provided,
        } = this.props

        const oid = this.getQuestionOid(question)

        return (
            <div
                className={`question-list-item-presentation ${className}`}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
            >
                <div className="survey-builder-question-list-item-header-presentation failed">
                    <div className={`question-list-item-left-presentation q_${oid}`} />
                    <div className="question-list-item-right-presentation">
                        <div className="question-list-item-right-top-presentation failed">
                            <span className={`survey-builder-question-header-row ${className}`}>
                                <div className="survey-builder-icons-container">
                                    <i className="fal fa-exclamation-triangle" aria-hidden="true" />
                                    {titleContent}
                                </div>
                                <div className="survey-builder-icons-container">
                                    <i className="far fa-sync" onClick={reloadQuestion} title={i18n.t('s_alt_q_reload')} />
                                </div>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    getQuestionOid = (question) => question.questionoid ?? question.question?.metadata?.oid

    renderQuestionItemLite = () => {
        const {
            question,
            titleContent,
            className,
            onClick,
            addOptionButton,
        } = this.props

        const oid = this.getQuestionOid(question)

        return (
            <div className={`question-list-item-presentation ${className}`}>
                <div className="survey-builder-question-list-item-header-presentation lite">
                    <div className={`question-list-item-left-presentation q_${oid}`} />
                    <div className="question-list-item-right-presentation">
                        <div className="question-list-item-right-top-presentation">
                            <div
                                className={`chevron-container ${this.open ? 'expanded' : ''}`}
                                style={{ marginInline: 10 }}
                                onClick={onClick}
                            >
                                <i className={`fa ${this.open ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
                            </div>
                            <span className={`survey-builder-question-header-row ${className}`}>
                                <div className="survey-builder-icons-container">
                                    {question.usedInOtherSurveys && (
                                        <i className="fa-light fa-link" />
                                    )}
                                    {titleContent}
                                </div>
                                {addOptionButton()}
                            </span>
                        </div>
                    </div>
                </div>
                <AnimateHeight
                    duration={250}
                    height={this.open ? 'auto' : 0}
                    className={this.open ? 'question-content' : ''}
                >
                    <div className="question-content-preview border-style">
                        {this.renderContent()}
                    </div>
                </AnimateHeight>
            </div>
        )
    }

    render() {
        const { isLite, isFailed } = this.props
        let render = this.renderQuestionItemFull
        if (isLite) {
            render = this.renderQuestionItemLite
        } else if (isFailed) {
            render = this.renderQuestionItemFailed
        }
        return (
            <Fragment>
                {render()}
            </Fragment>
        )
    }
}
